import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled'
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined'
import { sunkenColor } from '../../configuration/orders/detail/detailSurfaces'

// Story 204 — the three looks SystemValueChip can take. Moved here VERBATIM (comments included) from
// OperationStatusCell.jsx (StatusPill), JobDetailPrimitives.jsx (Pill) and BomStatusChip.jsx, so that every screen that
// paints a system value shares one text rule and keeps the exact look it had (RN1.5, S69).

// RN35: status is never told by color alone — one icon per OperationStatus value, always paired
// with its translated text (operationStatus.js).
// ADDENDUM 33 (E39) / S449 — un icono por valor, ocho. RN35: el color nunca es el único portador.
//
// Los TRES ACTIVOS son la misma familia de círculo a propósito —reproducir, pausa, llave— porque
// van a salir juntos en la misma tabla y se tienen que leer como variantes de una misma cosa sin
// aprender nada nuevo. Los tres NO ARRANCADOS tienen formas deliberadamente distintas entre sí
// (reloj de arena, puntos suspensivos, candado) porque comparten el neutro y es la FORMA la que
// los separa.
//
// ⚠️ `not-available` usa un CANDADO y no un icono de prohibido: el de prohibido (círculo con
// barra) se parece demasiado al de cancelada (círculo con aspa) y son los dos estados que más
// conviene no confundir — uno es "no se puede empezar todavía", el otro "ya no se hará".
const STATUS_ICONS = {
  finished: CheckCircleIcon,
  cancelled: CancelIcon,
  interrupted: PauseCircleFilledIcon,
  'active-execution': PlayCircleFilledIcon,
  'active-preparation': BuildCircleIcon,
  waiting: HourglassEmptyIcon,
  pending: PendingOutlinedIcon,
  'not-available': LockOutlinedIcon
}

// Pasada 18 (§6.2, mockup 02-operaciones.png): the same 4 status keys as a fixed legend, plus the
// per-status colour the row's left border and chip both borrow from — status is still always
// paired with its icon+text (RN35), colour is only ever a REPEAT of that, never its substitute.
// `pending` (addendum 12) comparte el neutro con `not-planned` a propósito: el color aquí solo
// REPITE lo que ya dicen el icono y el texto (RN35), así que compartirlo no quita información, y
// darle un color semántico propio insinuaría una urgencia que el dato no afirma.
// ⚠️ ADDENDUM 24 (E30): `interrupted` apunta a la entrada `pending` de la PALETA, que es un ámbar
// (#FFBF00). El nombre de esa clave del tema es un accidente histórico y NO tiene nada que ver con
// el estado PENDING de esta ficha —que usa el neutro—: es la única entrada compartida por los dos
// modos del tema que se separa del azul de "En curso". `yellow` existe solo en una rama del tema y
// el modo oscuro es el de por defecto, así que no sirve.
//
// MEDIDO en pantalla sobre los chips renderizados (modo oscuro, fondo #212121), no estimado:
// ΔE de Interrumpida contra Planificado **43,3**; contra En curso 132,5. De referencia, el par
// Finalizado/En curso que ya convivía está en 89,4. Contraste sobre el fondo: Interrumpida 9,7:1 y
// Planificado 5,2:1, las dos por encima de AA.
//
// ⚠️ Ámbar y naranja son hues VECINOS, así que Interrumpida/Planificado es el par más próximo de la
// leyenda —menos de la mitad de separación que el verde/azul que ya estaba— y es el que más se
// acerca con deuteranopia o protanopia. Lo que lo salva NO es el color: el icono es distinto (pausa
// frente a reloj) y el rótulo va al lado. Es exactamente el caso para el que existe RN35 —el estado
// nunca depende solo del color— y por eso aquí se sostiene. Si algún día hay que apretar, el sitio
// es la LEYENDA, no la fila.
// ═══ ADDENDUM 36 — EL CRITERIO ES PARIDAD CON MOS, NO DISTINGUIBILIDAD ═══
//
// ⚠️ LA MEDICIÓN ANTERIOR (ΔE CIE76) QUEDA SIN EFECTO COMO CRITERIO. Justificaba una paleta elegida
// por nosotros para SEPARAR ocho estados; el PO ha decidido que el criterio es PARECERSE a la
// pantalla que el operario ya usa. Lo que sigue valiendo de aquello es el ICONO POR VALOR, que es
// lo que hace legible que varios estados compartan color.
//
// Copiado de `JobStatusChip.jsx` (MOS) — el CRITERIO, no el código, que compara el literal con
// espacio y es de otra pantalla:
//     FINISHED        -> 'primary'    #7FE1A7 verde   (evaluado del tema, no leído a ojo)
//     INTERRUPTED     -> 'warning'    #ED6C02 naranja
//     NOT AVAILABLE   -> 'default'    gris
//     todo lo demás   -> 'info'       #0688D1 azul
// y coincide con los cuatro que el PO enumeró: ejecución azul, pendiente azul, interrumpida
// naranja, finalizada verde.
//
// ⚠️ `active-preparation` COMPARTE EL AZUL CON `active-execution`, y NO es un hueco: es una
// decisión del PO del 2026-09-03, escrita en el test de MOS — "los dos modos activos se distinguen
// SOLO POR TEXTO, el color no cambia". Los distingue el icono (reproducir frente a llave), que es
// lo que RN35 exige.
//
// ⚠️ `finished` pasa de `success` (#66bb6a, el verde POR DEFECTO de MUI) a `primary` (#7FE1A7, el
// que MIC define y el que usa MOS). El tema oscuro NO define `success`, así que estábamos pintando
// un verde que no es de la casa.
//
// ⚠️⚠️ `cancelled` ES EL ÚNICO ESTADO EN QUE NOS SEPARAMOS DE MOS, Y ESTÁ DECIDIDO ASÍ POR EL PO.
//
// Y EL MATIZ IMPORTA, porque sin él esto parece un descuido y alguien lo "corregirá" hacia el azul
// la próxima vez que compare las dos pantallas: NO DISCREPAMOS DEL CRITERIO DE MOS — es que AHÍ NO
// HAY CRITERIO. `CANCELLED` no tiene `case` propio en el `switch` de `JobStatusChip`: cae en la
// rama `default`, la de TODO LO DESCONOCIDO, y sale azul por omisión. Copiar eso sería copiar un
// accidente, no una decisión.
//
// Y el fondo: `CANCELLED` es un estado TERMINAL como `FINISHED`, no uno de los "aún no ha
// empezado". Pintarlo del mismo azul que una operación EN EJECUCIÓN diría que se está trabajando en
// algo que ya no se hará. La paridad se rompe solo donde MOS no decidió nada, que es exactamente
// donde debe romperse.
const STATUS_COLOR_KEY = {
  finished: 'primary',
  cancelled: 'error',
  interrupted: 'warning',
  'active-execution': 'info',
  'active-preparation': 'info',
  waiting: 'info',
  pending: 'info',
  'not-available': 'text'
}

const statusColorOf = (theme, iconKey) => {
  const colorKey = STATUS_COLOR_KEY[iconKey] || 'text'
  // ⚠️ La guarda no es decorativa y la destapó el addendum 24: `pending` es una entrada PROPIA del
  // tema de MIC, no del tema por defecto de MUI. Sin este `?.`, montar el bloque bajo cualquier tema
  // que no sea el de la aplicación —los tests, por ejemplo— reventaba la tabla ENTERA con un
  // "Cannot read properties of undefined", no solo el color de una píldora. Un color que falta se
  // cae al neutro; un estado sin color sigue teniendo su icono y su texto, que es lo que RN35 exige
  // de verdad.
  const custom = theme.palette[colorKey]
  return colorKey === 'text' || !custom?.main ? theme.palette.text.secondary : custom.main
}

// Un estado SIN color semántico (hoy "Not planned") no puede teñirse de su propio color: `alpha()`
// sobre `text.secondary` daba un blanco superpuesto al 12%, justo la familia que la escala de
// elevación destierra (pasada 25). Su relleno es el nivel hundido, opaco como todo lo demás; lo que
// lo distingue sigue siendo su icono y su borde, no un gris distinto.
const statusChipFill = (theme, iconKey) =>
  ((STATUS_COLOR_KEY[iconKey] || 'text') === 'text'
    ? sunkenColor(theme)
    : alpha(statusColorOf(theme, iconKey), 0.12))

/**
 * LA PÍLDORA DE ESTADO — solo el ASPECTO: icono, color, tamaño, radio y tipografía.
 *
 * ⚠️⚠️ SEPARADA DE CUALQUIER MAPA DE ESTADOS a propósito (petición del PO del 23/09): Workflow y
 * Progress la usan con el estado de la OPERACIÓN (`operationStatus.js`), y Piezas con el estado del
 * PASO DE UNA PIEZA (`partTrackStepState.js`). Es el mismo job visto para una pieza, y por eso los dos
 * dicen lo mismo con las mismas claves; lo que no se comparte es el MAPA, porque los enums no son el
 * mismo —el de la pieza solo tiene tres valores, y `DONE` no existe en el otro—.
 * RN35: siempre icono y texto; el color solo los repite.
 *
 * @param {{iconKey: string, label: string, testId?: string, iconTestIdPrefix?: string}} props
 *   `iconKey` es una clave de `STATUS_ICONS`; una desconocida sale sin icono y en neutro.
 */
export const ChipLook = ({ iconKey = '', label = '', testId = '', iconTestIdPrefix = 'operation-status-icon' }) => {
  const theme = useTheme()
  const Icon = STATUS_ICONS[iconKey]
  const color = statusColorOf(theme, iconKey)
  return (
    <Chip
      data-testid={testId || undefined}
      data-icon-key={iconKey || ''}
      size='small'
      icon={Icon ? <Icon data-testid={`${iconTestIdPrefix}-${iconKey}`} sx={{ fontSize: '14px !important', color: `${color} !important` }} /> : undefined}
      label={label}
      sx={{
        // Lienzo: 12,5 px a peso 600 y el aro del mismo color al 32 % (§2.5: relleno tintado del
        // propio color, nunca sólido bajo texto de color).
        fontSize: 12.5,
        fontWeight: 600,
        color,
        backgroundColor: statusChipFill(theme, iconKey),
        border: `1px solid ${alpha(color, 0.32)}`
      }}
    />
  )
}
ChipLook.propTypes = { iconKey: PropTypes.string, label: PropTypes.string, testId: PropTypes.string, iconTestIdPrefix: PropTypes.string }

/** Ex `Pill` (JobDetailPrimitives.jsx): chip pequeño en versalitas con color semántico (PENDIENTE, HECHA, ALTA…). */
export const OverlineLook = ({ label = '', tone = 'default', icon = null, testId = '' }) => {
  const palette = {
    default: { bg: 'action.selected', fg: 'text.secondary' },
    success: { bg: 'rgba(127, 225, 167, 0.16)', fg: 'primary.main' },
    warning: { bg: 'rgba(250, 204, 21, 0.16)', fg: 'yellow.main' },
    error: { bg: 'rgba(255, 99, 99, 0.18)', fg: 'error.light' },
    info: { bg: 'rgba(66, 165, 245, 0.18)', fg: 'info.light' }
  }[tone] || { bg: 'action.selected', fg: 'text.secondary' }
  return (
    <Box
      component='span'
      data-testid={testId || undefined}
      data-tone={tone}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1.5,
        bgcolor: palette.bg,
        color: palette.fg,
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      }}
    >
      {icon}
      {label}
    </Box>
  )
}

OverlineLook.propTypes = { label: PropTypes.string, tone: PropTypes.string, icon: PropTypes.node, testId: PropTypes.string }

/**
 * Ex `BomStatusChip`: the status pill of a BOM, the way the mockup 'MIC Biblioteca de BOMs' paints it (PO, 2026-09-15):
 * a soft tint of the status colour as background, the same colour at .35 as border, the label in the
 * colour itself. MUI's filled chip read as a solid block next to the rest of the row.
 * `color` is a palette key ('warning' | 'info' | 'success' | 'default').
 */
export const OutlinedLook = ({ label = '', color = 'default', testId = 'bom-status-chip' }) => (
  <Chip
    size='small'
    variant='outlined'
    label={label}
    data-testid={testId}
    sx={(theme) => {
      const main = color === 'default' ? theme.palette.text.secondary : theme.palette[color].main
      return {
        color: main,
        borderColor: alpha(main, 0.35),
        bgcolor: alpha(main, 0.14),
        fontWeight: 600
      }
    }}
  />
)
OutlinedLook.propTypes = { label: PropTypes.string, color: PropTypes.string, testId: PropTypes.string }
